import { Button, Text } from "@chakra-ui/react";
import moment, { Moment } from "moment";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { useSelector } from "react-redux";

import { PlusIcon } from "@nens/ranalib";
import { ResponseError } from "@nens/threedi/openapi-client";
import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import DatetimePicker from "../components/DatetimePicker";
import ErrorMessage from "../components/ErrorMessage";
import { RAIN_RASTERS } from "../data/rasterForecastRainRasters";
import { useLizardAggregate, useLizardAggregates } from "../hooks/useLizardAggregates";
import { remindUserToPressPlay } from "../state/mainGui";
import { getSimulation, getSimulationStatus } from "../state/simulation";
import { getModelPolygon } from "../state/simulation";
import inputElementStyles from "../styles/InputElements.module.css";
import { buttonRibbonStyle } from "../styles/styleComposition";
import { RainRaster, RasterAggregateResults, RasterType } from "../types/rasterRain";
import {
  dayHasRainInRasterAggregationresult,
  getRasterStartMoment,
  getRasterStopMoment,
} from "../utils/rainRasterHelper";
import { getDurationInSeconds } from "../utils/timeConverter";

import { FormLabel } from "./FormLabel";
import RainMenuRasterPreview from "./RainMenuRasterPreview";

const toRainRasterLizardUnit = (unit: api.RasterRainUnitsEnum): api.LizardUnitsEnum | undefined => {
  switch (unit) {
    case api.RasterRainUnitsEnum.M_S:
      return api.LizardUnitsEnum.M_S;
    case api.RasterRainUnitsEnum.MM_H:
    case api.RasterRainUnitsEnum.MM_HR:
      return api.LizardUnitsEnum.MM_H;
    default:
      return undefined;
  }
};

const RainMenuRaster: React.FC<{ type: RasterType } & ReturnType<typeof mapDispatchToProps>> = ({
  remindUserToPressPlay,
  type,
}) => {
  const rasters = Object.fromEntries(
    Object.entries(RAIN_RASTERS).filter(([_, raster]) => raster.type === type)
  );
  const firstRaster = Object.values(rasters)[0];

  const [rainRaster, setRainRaster] = useState<RainRaster>(firstRaster);

  const selectedRasterEarliest = getRasterStartMoment(rainRaster);
  const selectedRasterLatest = getRasterStopMoment(rainRaster);
  // https://github.com/nens/threedi-live-site/issues/1047
  // https://stackoverflow.com/questions/23263380/deprecation-warning-moment-construction-falls-back-to-js-date-when-trying-to
  // use .toISOString() see comment Bruno Finger and asnwer Joe Wilson
  const defaultStartDate = moment(new Date().toISOString());
  let defaultEndDate = moment(new Date().toISOString());
  if (type === "radar") {
    // From 25 hours ago to 24 hours ago
    defaultStartDate.subtract({ day: 1 });
    defaultStartDate.subtract({ hour: 1 });
    defaultEndDate.subtract({ day: 1 });
  } else {
    // type === "forecast"
    // From now to 24 hours from now
    defaultEndDate = selectedRasterLatest;
  }
  // Editable fields
  const [startDate, setStartDate] = useState<Moment>(defaultStartDate);
  const [endDate, setEndDate] = useState<Moment>(defaultEndDate);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [communicationError, setCommunicationError] = useState<boolean>(false);
  const [authorisationError, setAuthorisationError] = useState<boolean>(false);

  const modelPolygon = useSelector(getModelPolygon);
  const rasterAggregates: RasterAggregateResults = useLizardAggregates(
    RAIN_RASTERS,
    modelPolygon || ""
  );
  const selectedRasterAggregateQueryResult = useLizardAggregate(
    rainRaster.uuid,
    modelPolygon || "",
    (() => {
      const clone = startDate.clone();
      clone.utc();
      return clone.format();
    })(),
    (() => {
      const clone = endDate.clone();
      clone.utc();
      return clone.format();
    })(),
    rainRaster.frequency,
    rainRaster.unit
  );

  // Current application state
  const simulation = useSelector(getSimulation);
  const simulationStatus = useSelector(getSimulationStatus);

  if (!simulation) return null; // Shouldn't happen

  const getOffset = (): number => {
    // We let the rain start at the current simulation time
    return simulationStatus.time || 0;
  };

  const addRainRastersLizardEventToSimulation = () => {
    const offset = getOffset();
    const duration = getDurationInSeconds(startDate, endDate);
    if (!startDate || !duration) {
      // Validate checked that they are not empty, this is for TS
      return;
    }
    if (simulation === null) return;
    const rainRasterSimulationId = simulation.id;
    if (rainRasterSimulationId === undefined) return;

    // make request details
    const v3Api = getV3Api();
    const lizardUnit = toRainRasterLizardUnit(rainRaster.unit);
    if (lizardUnit === undefined) return;
    const eventsRainRastersLizardCreateRequest = {
      simulationPk: rainRasterSimulationId,
      data: {
        referenceUuid: rainRaster.uuid,
        offset: offset,
        duration: duration,
        units: lizardUnit,
        startDatetime: startDate.toDate(),
      },
    };

    setSubmitting(true);

    v3Api
      .simulationsEventsRainRastersLizardCreate(eventsRainRastersLizardCreateRequest)
      .then(() => {
        // Reset form
        setCommunicationError(false);
        setSubmitting(false);
      })
      .catch(async (error: ResponseError) => {
        const response = error.response;
        const parsedResponse = await response.json();

        // First check if it's an authorisation issue to access Lizard rain API
        if (
          response.status === 400 &&
          parsedResponse &&
          Array.isArray(parsedResponse) &&
          parsedResponse.length > 0 &&
          (parsedResponse[0] as string).includes("Reference_uuid could not be found in Lizard API")
        ) {
          setAuthorisationError(true);
          setSubmitting(false);
          console.error(
            "sending rain request failed with request",
            eventsRainRastersLizardCreateRequest,
            "with error:",
            parsedResponse
          );
        } else {
          setCommunicationError(true);
          setSubmitting(false);
          console.error(
            "sending rain request failed with request",
            eventsRainRastersLizardCreateRequest,
            "with error:",
            parsedResponse
          );
        }
      });

    remindUserToPressPlay();
  };

  const renderDay = (
    props: React.TdHTMLAttributes<HTMLTableCellElement>,
    currentDate: Moment,
    _selectedDate: Moment
  ) => {
    const rasterAggregate = rasterAggregates[rainRaster.key];
    const dayhasRain =
      rasterAggregate && dayHasRainInRasterAggregationresult(currentDate, rasterAggregate);
    return (
      <td
        style={
          dayhasRain
            ? {
                borderRadius: "50%",
                backgroundColor: "rgb(3 164 255)",
                height: "100%",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : {}
        }
        title={dayhasRain ? "Blue circle indicates rainfall is present on this day" : undefined}
        {...props}
      >
        {currentDate.date()}
      </td>
    );
  };

  return (
    <div>
      <FormLabel>
        {type === "radar" ? (
          <FormattedMessage id="newRainMenu.radar" defaultMessage="Radar" />
        ) : (
          <FormattedMessage id="newRainMenu.forecast" defaultMessage="Forecast" />
        )}
      </FormLabel>
      <select
        className={`${inputElementStyles.Padding} ${inputElementStyles.Input}`}
        value={rainRaster.key}
        onChange={(event) => {
          if (event.target.value in rasters) {
            const rainRaster = rasters[event.target.value];
            setRainRaster(rainRaster);
            const latestDate = getRasterStopMoment(rainRaster);
            if (endDate.isAfter(latestDate)) {
              setEndDate(latestDate);
            }
            if (startDate.isAfter(latestDate)) {
              const newStartDate = moment(latestDate);
              newStartDate.subtract({ hour: 4 });
              setStartDate(newStartDate);
            }
          }
        }}
      >
        {Object.entries(rasters).map(([key, rainRaster]) => (
          <option key={key} value={key}>
            {rainRaster.displayValue}
          </option>
        ))}
      </select>
      {rasters[rainRaster.key] && rasters[rainRaster.key].description ? (
        <Text fontSize="12px" mt="4px" color="brand.clear_creek" fontStyle="italic">
          {rasters[rainRaster.key].description}
        </Text>
      ) : null}
      <FormLabel>
        <FormattedMessage id="newRainMenu.from_label" defaultMessage="From" />
      </FormLabel>
      <DatetimePicker
        isValidDate={(currentDate) =>
          currentDate.isSameOrAfter(selectedRasterEarliest, "day") &&
          currentDate.isSameOrBefore(selectedRasterLatest, "day")
        }
        onDatetimeChange={(dt) => {
          let momentStartDate;
          if (typeof dt === "string") {
            momentStartDate = moment(dt);
          } else {
            momentStartDate = dt;
          }
          const newDuration = getDurationInSeconds(momentStartDate, endDate);
          if (newDuration <= 0) {
            const newEndtime = moment(momentStartDate);
            newEndtime.add({ seconds: 3600 });
            setStartDate(momentStartDate);
            setEndDate(newEndtime);
          } else {
            setStartDate(momentStartDate);
          }
        }}
        // https://github.com/nens/threedi-live-site/issues/1047
        // https://stackoverflow.com/questions/23263380/deprecation-warning-moment-construction-falls-back-to-js-date-when-trying-to
        // use .toISOString() see comment Bruno Finger and asnwer Joe Wilson
        selectedDate={startDate ? startDate.toISOString() : undefined}
        renderDay={renderDay}
      />
      <FormLabel>
        <FormattedMessage id="newRainMenu.to_label" defaultMessage="To" />
      </FormLabel>
      <DatetimePicker
        isValidDate={(currentDate) =>
          currentDate.isSameOrAfter(selectedRasterEarliest, "day") &&
          currentDate.isSameOrBefore(selectedRasterLatest, "day")
        }
        onDatetimeChange={(dt) => {
          let momentEndDate;
          if (typeof dt === "string") {
            momentEndDate = moment(dt);
          } else {
            momentEndDate = dt;
          }
          const newDuration = getDurationInSeconds(startDate, momentEndDate);
          if (newDuration <= 0) {
            const newStartDate = moment(momentEndDate);
            newStartDate.subtract({ seconds: 3600 });
            setStartDate(newStartDate);
            setEndDate(momentEndDate);
          } else {
            setEndDate(momentEndDate);
          }
        }}
        // https://github.com/nens/threedi-live-site/issues/1047
        // https://stackoverflow.com/questions/23263380/deprecation-warning-moment-construction-falls-back-to-js-date-when-trying-to
        // use .toISOString() see comment Bruno Finger and asnwer Joe Wilson
        selectedDate={endDate ? endDate.toISOString() : undefined}
        renderDay={renderDay}
      />
      <RainMenuRasterPreview
        selectedRasterAggregateQueryResult={selectedRasterAggregateQueryResult}
        rainRaster={rainRaster}
      />
      <div className={buttonRibbonStyle}>
        <Button
          variant="secondary"
          onClick={addRainRastersLizardEventToSimulation}
          isDisabled={submitting}
          leftIcon={<PlusIcon />}
        >
          <FormattedMessage id="newRainMenu.create_rain_button" defaultMessage="Create" />
        </Button>
      </div>
      <ErrorMessage
        error={authorisationError}
        errorMessage={
          <FormattedMessage
            id="error.not_sufficient_right"
            defaultMessage="You do not have sufficient right to perform this request. Please contact the Service Desk for more information."
          />
        }
      />
      <ErrorMessage
        error={communicationError}
        errorMessage={
          <FormattedMessage id="communicationerror" defaultMessage="Failed to send request." />
        }
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
  };
};

export default connect(null, mapDispatchToProps)(RainMenuRaster);
